import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/i18n/LanguageContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { TenantProvider, TenantGate } from '@/contexts/TenantContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import LoginPage from '@/pages/LoginPage';
import HomePage from '@/pages/HomePage';
import CoachingIntelligenceDashboard from '@/pages/CoachingIntelligenceDashboard';
import NationalTeamDashboard from '@/pages/NationalTeamDashboard';
import USSoccerPortal from '@/pages/USSoccerPortal';
import USSoccerExecutiveDashboard from '@/pages/USSoccerExecutiveDashboard';
import CompetitiveIntelligence from '@/pages/CompetitiveIntelligence';
import ROISimulator from '@/pages/ROISimulator';
import BrandDependency from '@/pages/BrandDependency';
import AthletesPage from '@/pages/AthletesPage';
import TeamsPage from '@/pages/TeamsPage';
import MethodologyPage from '@/pages/MethodologyPage';
import ProcurementKit from '@/pages/ProcurementKit';
import AnalyticsDashboard from '@/pages/AnalyticsDashboard';
import BriefingPage from '@/pages/BriefingPage';
import ClubDemo from '@/pages/ClubDemo';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import WorldCupBrandReport from '@/pages/WorldCupBrandReport';
import TermsPage from '@/pages/TermsPage';
import LegalNoticePage from '@/pages/LegalNoticePage';
import WorldCupLanding from '@/pages/WorldCupLanding';
import PredictorPage from '@/pages/PredictorPage';
import { usePageViewTracker } from '@/hooks/useAnalytics';
import { SEOManager, LanguageURLSync } from '@/seo/SEOManager';

function App() {
  // White-label: Remove any third-party branding on mount
  useEffect(() => {
    const removeThirdPartyBranding = () => {
      const selectors = [
        '#emergent-badge',
        '[id*="emergent"]',
        'a[href*="emergent.sh"]'
      ];
      
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          if (el && el.parentNode) {
            el.parentNode.removeChild(el);
          }
        });
      });
    };
    
    removeThirdPartyBranding();
    const timer = setTimeout(removeThirdPartyBranding, 500);
    
    const observer = new MutationObserver(removeThirdPartyBranding);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

/**
 * Wraps a page component with TenantProvider + TenantGate + ProtectedRoute.
 * This ensures: auth check → tenant resolution → tenant access check.
 */
function TenantRoute({ children }) {
  return (
    <ProtectedRoute>
      <TenantProvider>
        <TenantGate>
          {children}
        </TenantGate>
      </TenantProvider>
    </ProtectedRoute>
  );
}

function AppRoutes() {
  usePageViewTracker();
  return (
    <>
      <SEOManager />
      <LanguageURLSync />
      <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/briefing" element={<BriefingPage />} />
      <Route path="/club-demo" element={<ClubDemo />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-and-conditions" element={<TermsPage />} />
      <Route path="/legal-notice" element={<LegalNoticePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/world-cup-brand-index" element={<WorldCupLanding />} />
      <Route path="/predictor" element={<PredictorPage />} />
      <Route path="/world-cup-brand-report" element={<WorldCupBrandReport />} />

      {/* Shared protected routes (not tenant-scoped) */}
      <Route path="/national-teams" element={<ProtectedRoute><NationalTeamDashboard /></ProtectedRoute>} />
      <Route path="/athletes" element={<ProtectedRoute><AthletesPage /></ProtectedRoute>} />
      <Route path="/teams" element={<ProtectedRoute><TeamsPage /></ProtectedRoute>} />
      <Route path="/methodology" element={<ProtectedRoute><MethodologyPage /></ProtectedRoute>} />
      <Route path="/athlete/:profileId" element={<ProtectedRoute><CoachingIntelligenceDashboard /></ProtectedRoute>} />

      {/* Tenant-scoped routes — :tenantId resolved from URL */}
      <Route path="/:tenantId" element={<TenantRoute><USSoccerPortal /></TenantRoute>} />
      <Route path="/:tenantId/executive-dashboard" element={<TenantRoute><USSoccerExecutiveDashboard /></TenantRoute>} />
      <Route path="/:tenantId/competitive-intelligence" element={<TenantRoute><CompetitiveIntelligence /></TenantRoute>} />
      <Route path="/:tenantId/roi-simulator" element={<TenantRoute><ROISimulator /></TenantRoute>} />
      <Route path="/:tenantId/brand-dependency" element={<TenantRoute><BrandDependency /></TenantRoute>} />
      <Route path="/:tenantId/analytics" element={<TenantRoute><AnalyticsDashboard /></TenantRoute>} />
      <Route path="/:tenantId/procurement-kit" element={<TenantRoute><ProcurementKit /></TenantRoute>} />
    </Routes>
    </>
  );
}

export default App;
